

import React, { useEffect } from "react";
import { Avatar, Tooltip, Space } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import { useItemsFine } from "../_actions/app/items";
import { getFileURL } from "../_helpers/image";
import { FaInstagram, FaLinkedinIn, FaFacebookF, FaYoutube } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";

function SocialAccountSelection({
    org_id,
    platform = null,
    value,
    onChange,
    onChangeFull = () => { },
    exclude = [],
    auto_configure = false,
}) {
    const {
        isSuccess: successSK,
        isLoading: loadingSK,
        data: dataSK,
    } = useItemsFine(
        "qdb_social_keys",
        `filter[organization][_eq]=${org_id}&fields=id,account_id,profile_picture,platform,profile_info`,
        true
    );

    const filteredAccounts = successSK
        ? dataSK.filter((i) => {
            if (exclude?.includes(i.id)) return false;
            return platform
                ? i.platform?.toLowerCase() === platform.toLowerCase()
                : true;
        })
        : [];

    useEffect(() => {
        // Auto configure: select the first available account if value is empty
        if (auto_configure && (!value || (Array.isArray(value) && value.length === 0))) {
            if (filteredAccounts?.length > 0) {
                onChange([filteredAccounts[0].id]);
                onChangeFull([filteredAccounts[0]]);
            }
        }
    }, [auto_configure, filteredAccounts, value, onChange, onChangeFull]);

    // Format value as an array (allows for seamless transition if string was used)
    const selectedIds = Array.isArray(value) ? value : (value ? [value] : []);

    const toggleSelection = (id) => {
        let newIds;
        if (selectedIds.includes(id)) {
            // Deselect
            newIds = selectedIds.filter((selectedId) => selectedId !== id);
        } else {
            // Select
            newIds = [...selectedIds, id];
        }
        onChange(newIds);
        onChangeFull(filteredAccounts.filter((item) => newIds.includes(item.id)));
    };

    if (loadingSK) {
        return (
            <Space size="middle">
                <Avatar size={48} />
                <Avatar size={48} />
            </Space>
        );
    }

    if (successSK && filteredAccounts.length === 0) {
        return <div style={{ color: "gray" }}>No accounts found.</div>;
    }

    return (
        <Space wrap size="middle" style={{ marginBottom: "20px" }}>
            {filteredAccounts.map((item) => {
                const isSelected = selectedIds.includes(item.id);
                const name = item?.profile_info?.name || `${item.platform} User`;
                const avatarSrc = item?.profile_picture ? getFileURL(item.profile_picture) : null;
                const platformColor = SOCIAL_PLATFORM_COLORS[item.platform] || "#1890ff";
                const PlatformIcon = SOCIAL_PLATFORM_ICONS[item.platform];

                return (
                    <Tooltip title={`${name} (${item.platform})`} key={item.id}>
                        <div
                            onClick={() => toggleSelection(item.id)}
                            style={{
                                cursor: "pointer",
                                position: "relative",
                                display: "inline-block",
                                opacity: isSelected ? 1 : 0.4,
                                transform: isSelected ? "scale(1.05)" : "scale(1)",
                                transition: "all 0.2s ease-in-out",
                            }}
                        >
                            <Avatar
                                size={48}
                                src={avatarSrc}
                                style={{
                                    border: isSelected ? `2px solid ${platformColor}` : "2px solid transparent",
                                    padding: isSelected ? "2px" : "0",
                                    backgroundColor: "white",
                                }}
                            >
                                {name.charAt(0)}
                            </Avatar>
                            {PlatformIcon && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: -4,
                                        left: -4,
                                        width: 20,
                                        height: 20,
                                        borderRadius: "50%",
                                        backgroundColor: platformColor,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        border: "2px solid white",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                                    }}
                                >
                                    <PlatformIcon style={{ color: "white", fontSize: 10 }} />
                                </div>
                            )}
                            {isSelected && (
                                <CheckCircleFilled
                                    style={{
                                        position: "absolute",
                                        bottom: -2,
                                        right: -2,
                                        color: platformColor,
                                        backgroundColor: "white",
                                        borderRadius: "50%",
                                        fontSize: 18,
                                    }}
                                />
                            )}
                        </div>
                    </Tooltip>
                );
            })}
        </Space>
    );
}

const SOCIAL_PLATFORM_COLORS = {
    Instagram: "#E1306C",
    Linkedin: "#0077B6",
    Facebook: "#1877F2",
    Twitter: "#1DA1F2",
    Youtube: "#FF0000",
};

const SOCIAL_PLATFORM_ICONS = {
    Instagram: FaInstagram,
    Linkedin: FaLinkedinIn,
    Facebook: FaFacebookF,
    Twitter: FaXTwitter,
    Youtube: FaYoutube,
};

export default SocialAccountSelection;